import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/dialogs/Modal';
import { riskDashboardApi } from '@/api/services/riskDashboard';
import { todayDateString } from '@/api/services/riskDashboard';
import { formatNumber } from '@/utils/format';

const CCY: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CHF: '₣', JPY: '¥' };

function SlipWidget({ label, value, theme, icon }: { label: string; value: string; theme: string; icon: string }) {
  return (
    <div className="text-center">
      <span className={`avatar avatar-md rounded-circle shadow-sm bg-${theme}-transparent d-inline-flex align-items-center justify-content-center`}>
        <i className={`${icon} fs-18 text-${theme}`} />
      </span>
      <div className="fs-24 mb-0 mt-1 fw-normal">{value}</div>
      <div className="text-muted fs-15">{label}</div>
    </div>
  );
}

export function SlippageSummaryModal({
  target,
  onClose,
  onMoreDetails,
}: {
  target: { platformId: number; serverName: string };
  onClose: () => void;
  onMoreDetails?: () => void;
}) {
  const today = todayDateString();
  const q = useQuery({
    queryKey: ['lp-slippage', target.platformId],
    queryFn: () => riskDashboardApi.getLpSlippageSummary({ startDate: today, endDate: today, dataSourceId: target.platformId }),
  });
  const s = q.data;
  const sym = CCY[s?.reportingCurrency ?? ''] ?? (s?.reportingCurrency ? `${s.reportingCurrency} ` : '');
  const val = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? 'N/A' : `${sym}${formatNumber(v, 5)}`);

  // Three rows of two half-width widgets (icon-top / label-below), matching legacy.
  const rows: { label: string; value: string; theme: string; icon: string }[][] = [
    [
      { label: 'Total Positive', value: val(s?.totalPositive), theme: 'success', icon: 'fe fe-credit-card' },
      { label: 'Total Negative', value: val(s?.totalNegative), theme: 'danger', icon: 'fe fe-credit-card' },
    ],
    [
      { label: 'Best Slippage', value: val(s?.topPositiveSlippage), theme: 'success', icon: 'ri-line-chart-line' },
      { label: 'Worst Slippage', value: val(s?.topNegativeSlippage), theme: 'danger', icon: 'ri-line-chart-line' },
    ],
    [
      { label: 'Best', value: s?.bestSymbol || 'N/A', theme: 'success', icon: 'ri-currency-line' },
      { label: 'Worst', value: s?.worstSymbol || 'N/A', theme: 'danger', icon: 'ri-currency-line' },
    ],
  ];

  return (
    <Modal show title={`Daily Slippage Stats: ${target.serverName}`} onClose={onClose}>
      {q.isLoading ? (
        <div className="text-center text-muted py-4">Loading…</div>
      ) : (
        <>
          {rows.map((pair, i) => (
            <div className="row" key={i}>
              <div className="text-center mx-auto p-1 w-50 border-end"><SlipWidget {...pair[0]} /></div>
              <div className="text-center mx-auto p-1 w-50"><SlipWidget {...pair[1]} /></div>
            </div>
          ))}
          {onMoreDetails && (
            <div className="text-center w-100 mt-2">
              <a href="#" className="text-primary" onClick={(e) => { e.preventDefault(); onMoreDetails(); }}>
                More details <i className="ri-share-box-line align-middle" />
              </a>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
